import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { ReddiReportPresentationDto } from '../../../models/reddi/reddi-report-presentation-round-dto'
import { SpinnerService } from '../../../services/spinner/spinner.service'
import { ReddiService } from '../../../services/reddi/reddi.service'
import {
  CategoryScale,
  Chart,
  ChartConfiguration,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js'
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix'
import { CommonService } from '../../../services/common/common.service'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import {
  GetReddiGraphColor,
  GetReddiResult,
  ResultToDisplayName,
  Result,
} from '../../../services/reddi/reddi-utils'
import { interval, ObservableInput, switchMap, throttleTime } from 'rxjs'
import { SlatGraphDto } from '../../../models/slat/slat-graph-dto'
import { distinctUntilChanged } from 'rxjs/operators'
import { Subscription } from 'rxjs/internal/Subscription'

Chart.register(
  MatrixController,
  MatrixElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
)

@Component({
  selector: 'app-reddi-graph',
  templateUrl: './reddi-graph.component.html',
  imports: [TranslatePipe],
  styleUrl: './reddi-graph.component.scss',
})
export class ReddiGraphComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('canvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>
  @Input() clientId!: string
  @Input() autoRefresh: boolean = false
  reddiReportPresentationDto: ReddiReportPresentationDto[] = []

  private chart?: Chart

  constructor(
    public spinnerService: SpinnerService,
    public reddiService: ReddiService,
    private commonService: CommonService,
    private translateService: TranslateService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initialiseChart()
  }

  ngOnDestroy() {
    this.stopAutoRefresh()
  }

  initialiseData(response: ReddiReportPresentationDto[]) {
    this.reddiReportPresentationDto = response as ReddiReportPresentationDto[]
  }

  initialiseChart() {
    if (this.clientId) {
      this.spinnerService.show()
      this.reddiService
        .getReportReddiPresentationRounds(this.clientId)
        .subscribe({
          next: (response) => {
            if (!response) {
              console.error('Service response is undefined.')
              return [] as ReddiReportPresentationDto[]
            }

            this.initialiseData(response)
            this.render()
            return response
          },
          error: (err) => {
            console.error('Error fetching details:', err)
            this.spinnerService.hide()
          },
          complete: () => {
            this.spinnerService.hide()
          },
        })
    }

    this.translateService.onLangChange.subscribe(() => {
      if (!this.chart) return
      setTimeout(() => {
        this.render()
      })
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.chart) {
      this.render()
    }

    if (changes['autoRefresh'] && this.autoRefresh) {
      this.startAutoRefresh()
    }

    // If autoRefresh becomes false, stop any existing refresh
    if (changes['autoRefresh'] && !this.autoRefresh) {
      this.stopAutoRefresh()
    }

    this.cd.markForCheck()
  }

  private render(): void {
    const rows = (this.reddiReportPresentationDto ?? [])
      .slice()
      .sort(
        (a, b) =>
          new Date(b.presentationDate).getTime() -
          new Date(a.presentationDate).getTime(),
      )

    // Fixed height per row for consistent appearance
    const ROW_HEIGHT = 26

    // Space for the top header (group labels) + bottom padding
    const HEADER_HEIGHT = 60
    const FOOTER_HEIGHT = 16

    const baseSlots = rows.length + 1
    const ySlots = baseSlots > 1 ? baseSlots + 1 : baseSlots

    const chartAreaHeight = ySlots * ROW_HEIGHT

    const targetCanvasHeight = HEADER_HEIGHT + chartAreaHeight + FOOTER_HEIGHT

    const canvas = this.canvasRef.nativeElement
    const container = canvas.parentElement
    canvas.width = container?.clientWidth ?? 1200
    canvas.height = targetCanvasHeight

    const ctx = canvas.getContext('2d')!

    const sections = [
      {
        key: 'left',
        label: this.commonService.translateSafe('Left (R device off)'),
        prefix: 'left',
      },
      {
        key: 'right',
        label: this.commonService.translateSafe('Right (L device off)'),
        prefix: 'right',
      },
      {
        key: 'everyday',
        label: this.commonService.translateSafe('Everyday'),
        prefix: 'everyday',
      },
    ]

    const sounds = ['m', 'or', 'oo', 'ah', 'ee', 'sh', 's']

    const yLabels = rows.map((_, i) => `row_${i}`)

    // Insert a spacer category between col 1 (meta_age) and col 2 (meta_date)
    const SPACER = '__gap__'
    const xLabels: string[] = ['meta_age', SPACER, 'meta_date']
    const groupBounds: Array<{ start: number; end: number; label: string }> = []
    sections.forEach((sec) => {
      const start = xLabels.length
      sounds.forEach((s) => {
        const snd = this.commonService.translateSafe(s)
        const key = `${sec.key}|${snd}`
        xLabels.push(key)
      })
      const end = xLabels.length - 1
      groupBounds.push({ start, end, label: sec.label })
    })

    type Cell = {
      x: string
      y: string
      value?: number | null
      text?: string
      backgroundColor?: string
      borderColor?: string
      borderWidth?: number
      width?: number
      height?: number
    }
    const cells: Cell[] = []

    const fmtDate = (iso: string) =>
      new Date(iso).toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
      })

    rows.forEach((r, rowIndex) => {
      const y = yLabels[rowIndex]

      // Column 1
      cells.push({
        x: 'meta_age',
        y,
        text: String(r.ageInMonths),
        backgroundColor: '#ffffff',
        borderColor: '#d9d9d9',
        borderWidth: 1,
        width: 46,
        height: ROW_HEIGHT,
      })

      // We do NOT push any cell for the spacer category (SPACER) — that creates the gap

      // Column 2
      cells.push({
        x: 'meta_date',
        y,
        text: fmtDate(r.presentationDate),
        backgroundColor: '#ffffff',
        borderColor: '#d9d9d9',
        borderWidth: 1,
        width: 78,
        height: ROW_HEIGHT,
      })

      sections.forEach((sec) => {
        sounds.forEach((s, idx) => {
          const prop = `${sec.prefix}${s.charAt(0).toUpperCase()}${s.slice(
            1,
          )}` as keyof ReddiReportPresentationDto
          const val = r[prop] as unknown as number | undefined
          const bg =
            val == null ? '#ffffff' : GetReddiGraphColor(val) ?? '#cccccc'
          const snd = this.commonService.translateSafe(s)
          const key = `${sec.key}|${snd}`
          const soundResult = this.getSoundResult(idx + 1, r)
          const cell: Cell = {
            x: key,
            y,
            value: val ?? null,
            text: soundResult,
            backgroundColor: bg,
            borderColor: '#ffffff',
            borderWidth: 1,
            width: 50,
            height: ROW_HEIGHT,
          }
          cells.push(cell)
        })
      })
    })

    const config: ChartConfiguration<'matrix'> = {
      type: 'matrix',
      data: {
        datasets: [
          {
            label: 'REDDI Heatmap',
            data: cells as any[],
            parsing: false,
            borderColor: '#fff',
            borderWidth: 1,
            backgroundColor: (c) => (c.raw as any)?.backgroundColor || '#fff',
            width: (c) => (c.raw as any)?.width ?? 40,
            height: (c) => (c.raw as any)?.height ?? ROW_HEIGHT,
          },
        ],
      },
      options: {
        animation: false,
        responsive: false,
        maintainAspectRatio: false,
        layout: { padding: { top: HEADER_HEIGHT } },
        scales: {
          x: {
            type: 'category',
            position: 'top',
            labels: xLabels,
            offset: true,
            grid: { display: false },
            border: { display: false },
            ticks: {
              maxRotation: 0,
              padding: 25,
              autoSkip: false,
              callback: (idx: any) => {
                const label = xLabels[idx as number]
                if (!label || label === SPACER) return '' // hide spacer tick
                if (label === 'meta_age')
                  return this.commonService.translateSafe('Age in Months')
                if (label === 'meta_date')
                  return this.commonService.translateSafe('Date')
                return label.split('|')[1]
              },
            },
          },
          y: {
            type: 'category',
            labels: yLabels,
            reverse: false,
            offset: true,
            border: { display: false },
            grid: { display: false },
            ticks: { display: false },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: () => '',
              label: (ctx) => {
                const raw: any = ctx.raw
                // No tooltip for spacer
                if (!raw || raw.x === SPACER) return ''
                const soundResult = raw.text
                if (raw?.x === 'meta_age' || raw?.x === 'meta_date') return ''
                const [secKey, sound] = (raw?.x ?? '').split('|')
                const secLabel =
                  sections.find((s) => s.key === secKey)?.label ?? secKey
                return soundResult
                  ? `${secLabel} • ${sound} • ${soundResult}`
                  : `${secLabel} • ${sound}`
              },
            },
          },
        },
      },
      plugins: [
        this.drawGroupHeadersPlugin(groupBounds),
        this.drawCellTextPlugin(),
      ],
    }

    if (this.chart) this.chart.destroy()
    this.chart = new Chart(ctx, config)
  }

  private drawGroupHeadersPlugin(
    groups: Array<{ start: number; end: number; label: string }>,
  ) {
    return {
      id: 'groupHeaders',
      afterDraw(chart: Chart) {
        const xScale = chart.scales['x']
        const ctx = chart.ctx
        const chartArea = chart.chartArea

        ctx.save()
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.font = '600 14px system-ui, -apple-system, Segoe UI, Roboto, Arial'
        ctx.fillStyle = '#333'

        groups.forEach((g, idx) => {
          const left = xScale.getPixelForValue(g.start - 0.5)
          const right = xScale.getPixelForValue(g.end + 0.5)
          const mid = (left + right) / 2

          ctx.fillText(g.label, mid, chartArea.top - 50)

          if (idx < groups.length - 1) {
            const x = xScale.getPixelForValue(g.end + 0.5)
            ctx.strokeStyle = '#e5e5e5'
            ctx.lineWidth = 2
            ctx.beginPath()
            ctx.moveTo(x, chartArea.top)
            ctx.lineTo(x, chartArea.bottom)
            ctx.stroke()
          }
        })

        ctx.restore()
      },
    }
  }

  private drawCellTextPlugin() {
    const plusThreshold = 866080007
    return {
      id: 'cellText',
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx } = chart
        const ds = chart.data.datasets[0] as any
        const meta = chart.getDatasetMeta(0)
        const offsetWidth: number = 25
        const offsetHeight: number = 10

        ctx.save()
        ctx.fillStyle = '#111'
        ctx.font = '600 12px system-ui, -apple-system, Segoe UI, Roboto, Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'

        meta.data.forEach((elem: any, i: number) => {
          const raw = ds.data[i] || {}
          const { x, y } = elem.getProps(['x', 'y'], true)
          let text = ''
          if (raw.x === 'meta_age' || raw.x === 'meta_date') {
            text = raw.text ?? ''
          } else if (raw.value != null) {
            text = raw.value > plusThreshold ? '' : '*'
          }

          if (text) {
            ctx.fillText(text, x + offsetWidth, y + offsetHeight)
          }
        })

        ctx.restore()
      },
    }
  }

  private getSoundResult(idx: number, round: any): string {
    if (idx < 1 || idx > 7) return ''
    const prop = `sound${idx}Result`
    let result = round[prop] as Result
    let text = ResultToDisplayName(result)
    return text ?? ''
  }

  private refreshSub?: Subscription

  private startAutoRefresh(): void {
    // Stop any previous interval before starting new one
    this.stopAutoRefresh()

    this.refreshSub = interval(this.commonService.getRefreshTimeOut())
      .pipe(
        throttleTime(this.commonService.getThrottleTimeOut()),
        switchMap(
          (
            value: number,
            index: number,
          ): ObservableInput<ReddiReportPresentationDto[]> => {
            if (this.clientId) {
              return this.reddiService.getReportReddiPresentationRounds(
                this.clientId,
              )
            }
            return null
          },
        ),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((response) =>
        this.initialiseData(response as ReddiReportPresentationDto[]),
      )
  }

  private stopAutoRefresh(): void {
    if (this.refreshSub) {
      this.refreshSub.unsubscribe()
      this.refreshSub = undefined
    }
  }
}
